import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Menu, X, Car, Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import AIChatAgent from "./AIChatAgent";
import PlanGoWidget from "./PlanGoWidget";
import CookieBanner from "./CookieBanner";
import LeadCapturePopup from "./LeadCapturePopup";
import Footer from "./Footer";
import { businessSchema, buildBreadcrumbSchema } from "./schemaData";

const Layout = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    Pakketten: true,
    Theorie: true,
  });
  const location = useLocation();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    if (location.hash) {
      // Retry a few times in case of lazy loading
      let attempts = 0;
      const scrollInterval = setInterval(() => {
        const id = location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          clearInterval(scrollInterval);
        }
        attempts++;
        if (attempts > 10) clearInterval(scrollInterval); // stop after 1 second
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Pakketten",
      path: "/rijlespakketten",
      submenus: [
        {
          name: "Welk pakket past bij mij",
          path: "/rijlespakketten#pakket-kiezen",
        },
        { name: "Termijn calculator", path: "/rijlespakketten#calculator" },
      ],
    },
    {
      name: "Theorie",
      path: "/theorie",
      submenus: [
        { name: "Test je theorie kennis", path: "/theorie#test-je-kennis" },
      ],
    },
    { name: "Over Ons", path: "/over-ons" },
    { name: "Vestiging", path: "/vestiging" },
    { name: "FAQ", path: "/faq" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const breadcrumbs = [
    { name: "Home", url: "https://rijschooldediamant.nl/" },
    ...pathSegments.map((segment, index) => {
      const url = `https://rijschooldediamant.nl/${pathSegments.slice(0, index + 1).join("/")}`;
      const name = segment
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      return { name, url };
    }),
  ];

  const breadcrumbSchema = buildBreadcrumbSchema(breadcrumbs);

  return (
    <div className="flex min-h-screen flex-col font-sans relative overflow-x-hidden">
      {/* Subtle Golden Beams */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] max-w-[600px] h-[50vw] max-h-[600px] bg-yellow-400/10 rounded-full blur-[100px] animate-breathe" />
        <div className="absolute top-[40%] right-[-10%] w-[40vw] max-w-[500px] h-[40vw] max-h-[500px] bg-amber-500/10 rounded-full blur-[120px] animate-breathe-slow" />
        <div className="absolute bottom-[-10%] left-[20%] w-[60vw] max-w-[700px] h-[60vw] max-h-[700px] bg-yellow-500/5 rounded-full blur-[150px] animate-breathe" />
      </div>

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify([businessSchema, breadcrumbSchema])}
        </script>
      </Helmet>
      {/* Top Banner (Scarcity) */}
      <div className="bg-primary text-primary-foreground py-2 px-4 text-sm font-bold flex items-center justify-center gap-2 text-left">
        🔥 Nog slechts 2 plekken voor een proefles!
        <Link
          to="/proefles"
          className="underline hover:text-white transition-colors"
        >
          Boek direct
        </Link>
      </div>

      {/* Top Bar */}
      <div className="bg-slate-900 text-slate-200 py-2 text-sm hidden md:block">
        <div className="container mx-auto px-4 flex justify-end items-center gap-6">
          <div className="flex items-center gap-4 text-slate-300 font-medium">
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <Link to="/faq" className="hover:text-white transition-colors">
              FAQ
            </Link>
          </div>
          <div className="flex items-center gap-4 border-l border-slate-700 pl-6">
            <span className="flex items-center">
              <MapPin className="w-4 h-4 mr-2 text-primary" />
              Alphen aan den Rijn e.o.
            </span>
          </div>
          <div className="flex items-center space-x-6 border-l border-slate-700 pl-6">
            <a
              href="tel:0172700509"
              className="flex items-center hover:text-primary transition-colors"
            >
              <Phone className="w-4 h-4 mr-2 text-primary" />
              0172 - 700 509
            </a>
            <a
              href="mailto:info@rijschooldediamant.nl"
              className="flex items-center hover:text-primary transition-colors"
            >
              <Mail className="w-4 h-4 mr-2 text-primary" />
              info@rijschooldediamant.nl
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled ? "bg-transparent py-2" : "bg-slate-950 py-2"
        }`}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link
            to="/"
            className={`flex items-center z-50 shrink-0 transition-all duration-300 ${isScrolled ? "w-[200px] md:w-[280px]" : "w-[300px] md:w-[432px]"}`}
            aria-label="Home"
          >
            <img
              src="https://vibe.filesafe.space/1781862609113466713/attachments/c36d85c1-49ed-4cf3-9e8d-89006f7035ac.png"
              alt="Logo Rijschool De Diamant - Dé autorijschool in Alphen aan den Rijn"
              width={432}
              height={100}
              fetchPriority="high"
              decoding="async"
              className={`w-full h-auto object-contain drop-shadow-xl relative z-50 transition-all duration-300 scale-95 md:scale-100 ${isScrolled ? "mt-2 mb-2" : "-mt-16 md:-mt-24 -mb-12 md:-mb-20"}`}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 bg-white/80 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none p-2 rounded-full">
            {navLinks
              .filter((link) => !["Blog", "FAQ"].includes(link.name))
              .map((link) => (
                <div key={link.path} className="relative group">
                  <Link
                    to={link.path}
                    className={`text-sm font-semibold transition-colors hover:text-primary py-2 ${
                      location.pathname === link.path
                        ? "text-primary"
                        : isScrolled
                          ? "text-slate-900 drop-shadow-md"
                          : "text-slate-300"
                    }`}
                  >
                    {link.name}
                  </Link>
                  {link.submenus && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 flex flex-col overflow-hidden">
                      {link.submenus.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className="px-4 py-3 text-sm text-slate-700 hover:text-primary hover:bg-slate-50 transition-colors"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            <Button
              asChild
              className="rounded-full px-6 shadow-md shadow-primary/20"
            >
              <Link to="/proefles">Proefles Aanvragen</Link>
            </Button>
          </nav>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2.5 bg-primary text-primary-foreground shadow-lg shadow-primary/20 drawn-border transition-transform hover:scale-105"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Menu sluiten" : "Menu openen"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 shadow-lg border-t border-slate-800 py-3 px-4 flex flex-col gap-2">
            {navLinks
              .filter((link) => !["FAQ", "Blog"].includes(link.name))
              .map((link) => (
                <div
                  key={link.path}
                  className="flex flex-col border-b border-slate-800 last:border-0 pb-1"
                >
                  <div className="flex items-center justify-between">
                    <Link
                      to={link.path}
                      className={`text-lg font-medium py-1.5 flex-1 ${
                        location.pathname === link.path
                          ? "text-primary"
                          : "text-slate-300"
                      }`}
                      onClick={() => !link.submenus && setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                    {link.submenus && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setExpandedMenus((prev) => ({
                            ...prev,
                            [link.name]: !prev[link.name],
                          }));
                        }}
                        className="p-2 -mr-2 text-slate-400 hover:text-primary transition-colors"
                        aria-label="Toggle submenu"
                      >
                        <ChevronDown
                          className={`w-5 h-5 transition-transform duration-200 ${expandedMenus[link.name] ? "rotate-180" : ""}`}
                        />
                      </button>
                    )}
                  </div>
                  {link.submenus && expandedMenus[link.name] && (
                    <div className="flex flex-col pl-4 border-l-2 border-slate-800 ml-2 mt-0 mb-2 animate-accordion-down overflow-hidden">
                      {link.submenus.map((sub) => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className="py-1.5 text-base text-slate-400 hover:text-primary"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            <Button asChild className="w-full mt-2" size="lg">
              <Link to="/proefles">Proefles Aanvragen</Link>
            </Button>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col pb-20 md:pb-0">
        <Outlet />
      </main>

      <Footer />

      <AIChatAgent />
      <PlanGoWidget />
      <CookieBanner />
      <LeadCapturePopup />
    </div>
  );
};

export default Layout;
